<script setup>
import { formatDate } from "@/utils/formatDate";
import { ref, onMounted, computed } from "vue";
import tableHeaders from "@/utils/tableHeaders.js";
import * as XLSX from "xlsx";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t } = useI18n();

const deadlineHeaders = computed(() =>
  tableHeaders.deadlineHeaders.map((header) => {
    return {
      ...header,
      title: t(header.title),
    };
  }),
);

const props = defineProps({
  construction: {
    type: Object,
    required: true,
  },
});
const tableData = ref([]);
const isLoading = ref(true);
const percentge = ref(0);

let subLocationIndex = computed(
  () => store.getters["appData/getCurrentSubLocationId"],
);

const {
  ConstructionStructureRequest,
} = require("../../../proto-out/IMultiStatisticsService-v5_pb");

const {
  MultiStatisticsServiceClient,
} = require("../../../proto-out/IMultiStatisticsService-v5_grpc_web_pb");

let authToken = computed(() => store.getters["users/tokenUser"]);
let serverUrl = computed(() => store.getters["appData/getServerUrl"]);

const fetchDataFromGrpc = async () => {
  try {
    const client = new MultiStatisticsServiceClient(serverUrl.value);
    const metadata = { Authorization: `Bearer ${authToken.value}` };

    const request = new ConstructionStructureRequest();
    request.setConstructioninstance(props.construction.actualinstance);
    request.setStructureid(subLocationIndex.value || 0);

    const getDetailedStructureExecutionData = await new Promise(
      (resolve, reject) => {
        client.getDetailedStructureExecution(
          request,
          metadata,
          (err, response) => {
            if (err) {
              console.error("gRPC Error:", err.message);
              reject(new Error("Failed to fetch data from gRPC"));
            } else {
              resolve(response.toObject());
            }
          },
        );
      },
    );
    tableData.value = getDetailedStructureExecutionData.valuesList;
    percentge.value = calculateAvgFact(
      getDetailedStructureExecutionData.valuesList,
    );
  } catch (error) {
    console.error(error);
  }
  isLoading.value = false;
};
onMounted(() => {
  fetchDataFromGrpc();
});

const calculateAvgFact = (arr) => {
  const total = arr.reduce((sum, obj) => sum + obj.fact, 0);
  return arr.length > 0 ? total / arr.length : 0;
};

const formatDateToReal = (seconds) => {
  const date = new Date(seconds * 1000);
  return date.toLocaleDateString();
};

const exportToExcel = () => {
  const headers = deadlineHeaders.value.map((header) => header.title);
  const rows = tableData.value.map((item) => {
    return deadlineHeaders.value.map((header) => {
      if (header.key === "date") {
        return item.date && item.date.seconds
          ? formatDateToReal(item.date.seconds)
          : "";
      } else if (header.key === "fact") {
        return item.fact !== null ? `${item.fact}%` : "";
      } else {
        return item[header.key] ?? "";
      }
    });
  });
  const worksheetData = [headers, ...rows];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  headers.forEach((_, colIdx) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    if (!worksheet[cellAddress]) return;
    worksheet[cellAddress].s = {
      font: { bold: true },
      alignment: { horizontal: "center" },
      fill: { fgColor: { rgb: "D9EAD3" } },
    };
  });

  worksheet["!cols"] = headers.map(() => ({ wpx: 150 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Отчет");

  const date = new Date();
  const dateString = date.toLocaleDateString("en-GB").replace(/\//g, "_");
  const fileName = `${dateString}_мултикейс_срок_отчет.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

const exportServerToExcel = () => {
  // https://novgorod.grpc.ibpspb.ru/export/StructureExecution/{constructionInstance}/{structureId}
  const url = `${serverUrl.value}export/StructureExecution/${props.construction.actualinstance}/${subLocationIndex.value}`;
  window.open(url, "_blank");
};
</script>

<template>
  <v-card class="custom-card" style="">
    <!-- Title Section -->
    <v-row>
      <v-col class="text-center pa-0" cols="12">
        <v-card-text class="title pa-0 pb-5">{{
          t("deadline_table.completion")
        }}</v-card-text>
      </v-col>
    </v-row>
    <!-- Data Table -->
    <v-data-table
      :headers="deadlineHeaders"
      :items="tableData"
      item-value="num"
      height="65vh"
      fixed-header
      hide-default-footer
      show-select
      density="compact"
      :items-per-page="-1"
      :loading="isLoading"
      :loading-text="t('loading_text')"
    >
      <template v-slot:no-data>
        <div class="text-center">
          <p>{{ t("No_data_text") }}</p>
        </div>
      </template>
      <template v-slot:[`item.date`]="{ item }">
        <span>
          {{
            item.date && item.date.seconds ? formatDate(item.date.seconds) : ""
          }}
        </span>
      </template>
      <template v-slot:[`item.fact`]="{ item }">
        <span>
          {{ item.fact !== null ? `${item.fact}%` : "" }}
        </span>
      </template>
    </v-data-table>

    <!-- Footer Section -->
  </v-card>
  <v-row class="pa-0 ma-0">
    <v-col class="text-right">
      <span class="bottom-text mr-5 up-text"
        >{{ t("deadline_table.average_completion_percentage") }}:
        {{ percentge?.toFixed(0) }}%</span
      >
      <v-btn @click="exportServerToExcel" class="mr-5 up-text" rounded="lg">{{
        t("export_in_excel")
      }}</v-btn>
      <v-btn class="up-text" rounded="lg">{{
        t("deadline_table.send_data")
      }}</v-btn>
    </v-col>
  </v-row>
</template>

<style scoped>
.title {
  color: #00a3ff;
  font-size: 34px;
  font-weight: bold;
}
.custom-card {
  background-color: transparent;
  height: 92%;
}
.bottom-text {
  color: #006aa6;
  font-size: 12px;
}
.up-text {
  text-transform: uppercase;
}
.v-data-table {
  background-color: transparent;
}

.v-data-table :deep(table) {
  border-spacing: 0 0.9rem !important;
}
:deep(.v-data-table thead th) {
  background: linear-gradient(#006aa6, #002940) !important;
  color: white !important;
  font-weight: bold !important;
  text-align: center !important;
  text-transform: uppercase;
}
:deep(.v-data-table thead th:first-child) {
  margin-bottom: 20px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}
:deep(.v-data-table thead th:last-child) {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}
:deep(.v-data-table__td:first-child) {
  border-radius: 10px 0 0 10px !important;
}
:deep(.v-data-table__td:last-child) {
  border-radius: 0 10px 10px 0 !important;
}
:deep(.v-data-table tbody tr) {
  background-color: #ececec;
  color: #00507d;
  font-size: 12px;
  text-transform: uppercase;
}
:deep(.v-data-table tbody tr):hover {
  background-color: #d9d9d9;
}
.custom-card :deep(::-webkit-scrollbar-track) {
  margin-top: 4em !important ;
}
@media screen and (max-width: 1750px) {
  .custom-card {
    height: 90%;
  }
}
</style>
